import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";
import {meService} from "@/src/modules/core/services/me";

import {companyVehiclesApi} from "../api";
import type {
  CompanyVehicle,
  CreateCompanyVehiclePayload,
  UpdateCompanyVehiclePayload,
  VehicleConditionCheckListItem,
  VehicleConditionCheckPayload,
  VehicleConditionCheckRow,
} from "../types";

function normalizeCompanyVehicle(row: CompanyVehicle & {company_vehicle_id?: number}): CompanyVehicle {
  const id = row.id ?? row.company_vehicle_id;
  if (id == null) return row;
  return {...row, id};
}

function parseVehicleConditionChecksListPayload(
  data: unknown,
): GetListResponse<VehicleConditionCheckListItem> {
  const raw = data as {
    data?: unknown;
    meta?: {
      total?: number;
      from?: number;
      to?: number;
      limit?: number;
      offset?: number;
      current_page?: number;
      last_page?: number;
    };
  };
  const list = Array.isArray(raw.data) ? (raw.data as VehicleConditionCheckListItem[]) : [];
  const m = raw.meta;
  const total = m?.total ?? 0;
  const from = m?.from ?? (list.length ? 1 : 0);
  const to = m?.to ?? (list.length ? list.length : 0);
  return {
    data: list,
    meta: {total, from, to},
  };
}

class CompanyVehiclesService {
  async getCompanyVehicles(
    options?: ParamsOptions,
  ): Promise<GetListResponse<CompanyVehicle>> {
    const {data} = await companyVehiclesApi.list(options);
    const raw = data as {
      data?: unknown;
      meta?: {total?: number; from?: number; to?: number};
    };
    const list = Array.isArray(raw.data) ? (raw.data as CompanyVehicle[]) : [];
    const m = raw.meta;
    const total = m?.total ?? 0;
    return {
      data: list.map(normalizeCompanyVehicle),
      meta: {
        total,
        from: m?.from ?? (list.length ? 1 : 0),
        to: m?.to ?? list.length,
      },
    };
  }

  async getCompanyVehicleById(id: number, options?: ParamsOptions): Promise<CompanyVehicle> {
    const {data} = await companyVehiclesApi.getById(id, options);
    return normalizeCompanyVehicle(
      (data as {data: CompanyVehicle}).data as CompanyVehicle & {company_vehicle_id?: number},
    );
  }

  async createCompanyVehicle(
    payload: CreateCompanyVehiclePayload,
  ): Promise<CompanyVehicle> {
    const {data} = await companyVehiclesApi.create(payload);
    return normalizeCompanyVehicle(
      (data as {data: CompanyVehicle}).data as CompanyVehicle & {company_vehicle_id?: number},
    );
  }

  async updateCompanyVehicle(
    id: number,
    payload: UpdateCompanyVehiclePayload,
  ): Promise<CompanyVehicle> {
    const {data} = await companyVehiclesApi.update(id, payload);
    return normalizeCompanyVehicle(
      (data as {data: CompanyVehicle}).data as CompanyVehicle & {company_vehicle_id?: number},
    );
  }

  async createConditionCheck(
    payload: VehicleConditionCheckPayload,
  ): Promise<unknown> {
    const {data} = await companyVehiclesApi.createConditionCheck(payload);
    return data;
  }

  /**
   * Lists condition checks (requires `vehicleconditioncheck show`). Filter by schedule `shift_id`
   * so the start-phase row resolves for the end check (aligns with POST body).
   */
  async getConditionChecksForShift(shiftId: number): Promise<VehicleConditionCheckRow[]> {
    const {data} = await companyVehiclesApi.listConditionChecks({
      page: {limit: 50, offset: 0},
      filter: {shift_id: shiftId},
    });
    const raw = data as {data?: unknown};
    const list = Array.isArray(raw.data) ? (raw.data as VehicleConditionCheckRow[]) : [];
    return list;
  }

  /**
   * Paginated list (`vehicleconditioncheck show`). Omit `context_property_id` unless API requires it.
   */
  async getVehicleConditionChecksList(
    options?: ParamsOptions,
  ): Promise<GetListResponse<VehicleConditionCheckListItem>> {
    const {data} = await companyVehiclesApi.listConditionChecks(options);
    return parseVehicleConditionChecksListPayload(data);
  }

  /** GET `company-vehicles/{companyVehicleId}/vehicle-condition-checks` */
  async getVehicleConditionChecksForCompanyVehicle(
    companyVehicleId: number,
    options?: ParamsOptions,
  ): Promise<GetListResponse<VehicleConditionCheckListItem>> {
    const {data} = await companyVehiclesApi.listConditionChecksForCompanyVehicle(
      companyVehicleId,
      options,
    );
    return parseVehicleConditionChecksListPayload(data);
  }

  async deleteCompanyVehicle(id: number): Promise<void> {
    await companyVehiclesApi.remove(id);
  }

  getCanListCompanyVehicles(): boolean {
    return meService.getHasPermission("companyvehicle show");
  }

  getCanShowCompanyVehicle(): boolean {
    return meService.getHasPermission("companyvehicle show");
  }

  getCanCreateCompanyVehicle(): boolean {
    return meService.getHasPermission("companyvehicle store");
  }

  getCanUpdateCompanyVehicle(): boolean {
    return meService.getHasPermission("companyvehicle update");
  }

  getCanDeleteCompanyVehicle(): boolean {
    return meService.getHasPermission("companyvehicle delete");
  }

  getCanCreateConditionCheck(): boolean {
    return meService.getHasPermission("vehicleconditioncheck store");
  }

  getCanListConditionChecks(): boolean {
    return meService.getHasPermission("vehicleconditioncheck show");
  }
}

export const companyVehiclesService = new CompanyVehiclesService();
